<template>
	<div class="widget widget-kpi widget-order-tracking" :class="{ 'fullscreen-widget': isFullscreen }" ref="widgetRootRef">
		<v-card class="mx-auto" flat>
			<template v-if="!isLoading">
				<v-btn v-if="isSheetLayout" class="widget-back-fab" icon variant="flat" :aria-label="t('Back')" @click="toggleFullscreen">
					<v-icon>chevron_left</v-icon>
				</v-btn>

				<v-toolbar color="transparent" density="compact" @click="toggleFullscreen">
					<div class="v-card-top-actions" v-if="isFullscreen && !isSheetLayout">
						<span>
							<v-icon>chevron_left</v-icon>
							{{ t('Back') }}
						</span>
					</div>
					<v-toolbar-title>
						<v-icon>query_stats</v-icon>
						{{ title }}
					</v-toolbar-title>
					<div class="v-card-top-actions">
						<v-icon v-if="!isFullscreen">arrow_circle_right</v-icon>
					</div>
				</v-toolbar>

				<v-card-text>
					<div v-show="!isFullscreen" class="widget-order-tracking__compact" @click="toggleFullscreen">
						<div class="d-flex align-center ga-2 flex-wrap justify-space-between">
							<ButtonToggleControl v-if="showMetricSwitch" v-model="metric" :items="metricItems" class="widget-order-tracking__switch" @click.stop />
							<v-chip v-if="periodLabel" color="primary" variant="tonal" readonly>
								<v-icon start>date_range</v-icon>
								{{ periodLabel }}
							</v-chip>
						</div>

						<span class="widget-order-tracking__hero-label">{{ heroLabel }}</span>

						<ProgressBarRenderer v-if="heroGauge.hasObjective" :params="heroParams" />

						<div v-else class="widget-order-tracking__hero">
							<span class="widget-order-tracking__hero-value">{{ heroFormatted }}</span>
							<span class="widget-order-tracking__hero-note">{{ t('NoObjective') }}</span>
						</div>

						<div class="widget-order-tracking__indicators">
							<v-chip v-if="showServiceRate" :color="serviceRate == null ? undefined : 'success'" variant="tonal" readonly>
								<v-icon start>check_circle</v-icon>
								{{ t('ServiceRate') }}
								<strong class="ml-1">{{ formatPercent(serviceRate) }}</strong>
							</v-chip>
							<span class="widget-order-tracking__orders">{{ orderCountLabel }}</span>
						</div>

						<ChartCard v-if="showEvolutionChart" :options="evolutionOptions" :is-empty="isEvolutionEmpty" fill-height hide-actions class="widget-order-tracking__chart" @click.stop />
					</div>

					<OrderTrackingBuilder v-if="isFullscreen" :title="title" :params="params" :guid="guid" />
				</v-card-text>
			</template>

			<template v-else>
				<v-card-text>
					<div class="widget-order-tracking__compact">
						<v-skeleton-loader type="list-item, list-item-two-line, list-item-two-line, list-item-two-line" />
						<v-skeleton-loader type="ossein" class="widget-order-tracking__chart" />
					</div>
				</v-card-text>
			</template>
		</v-card>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { log } from '@/utils/core/vueLogger.js';
import { eventBus } from '@/utils/core/eventBus';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { useVisibleRefresh } from '@/composables/useVisibleRefresh';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { presetRange, periodPresetLabel, resolvePeriodSelection } from '@/utils/domain/periodPresets';
import { getDirectOrders } from '@/api/sales/order';
import { getOrganisations } from '@/api/core/organisation';
import { METRIC, filterOrders, aggregatePeriod, aggregateByMonth, resolveObjectives, ownerOrganisationScope } from '@/services/directOrderService';
import { loadPeriodSelection } from '@/components/orderTracking/orderTrackingSettings';
import { formatPercent, formatNumber, formatPeriodLabel, formatMetric } from '@/utils/core/format';
import { buildGauge } from '@/utils/domain/objectiveGauge';
import { buildEvolutionOptions } from '@/components/orderTracking/orderTrackingCharts';
import ProgressBarRenderer from '@/components/agGrid/ProgressBarRenderer.vue';
import ButtonToggleControl from '@/components/controls/ButtonToggleControl.vue';
import ChartCard from '@/components/common/ChartCard.vue';
import OrderTrackingBuilder from '@/components/orderTracking/OrderTrackingBuilder.vue';

defineProps({
	title: { type: String, default: '' },
	params: { type: Object, default: () => ({}) },
	guid: { type: String, default: '' },
});

const t = useTranslator();
const userOrganisationId = iframeDataBridge.getCurrentUser()?.organisationId;

const widgetRootRef = ref(null);
const isLoading = ref(true);
const isFullscreen = ref(false);
const isTouchMode = ref(deviceUtils.isTouchMode());
const metric = ref(METRIC.VALUE);
const orders = ref([]);
const chartOrders = ref([]);
const savedPeriod = ref(null);
const objectives = ref({ available: false, period: null, value: { ordered: {}, delivered: {} }, weight: { ordered: {}, delivered: {} } });
const isSheetLayout = computed(() => isFullscreen.value && isTouchMode.value);
const metricObjectives = computed(() => objectives.value[metric.value] ?? { ordered: {}, delivered: {} });
const deliveredActive = computed(() => metricObjectives.value.delivered?.hasObjective || !metricObjectives.value.ordered?.hasObjective);

// The four KPI refs (value/weight × ordered/delivered) can each carry their own objective period, so
// the "current objective period" (RG-05) must follow whichever one is actually on screen — falling
// back to the blended period only when the active metric/base has none of its own.
const objectivePeriod = computed(() => {
	const source = deliveredActive.value ? metricObjectives.value.delivered : metricObjectives.value.ordered;
	return source?.period ?? objectives.value.period;
});

function presetPeriod(key) {
	const range = presetRange(key);
	return { start: range[0], end: range[1] };
}

const heroPeriod = computed(() => objectivePeriod.value ?? savedPeriod.value ?? presetPeriod('this-month'));
const chartPeriod = computed(() => (!objectivePeriod.value && savedPeriod.value ? savedPeriod.value : presetPeriod('last-6-months')));
const totals = computed(() => aggregatePeriod(orders.value));

const monthlySeries = computed(() => aggregateByMonth(chartOrders.value, { start: chartPeriod.value.start, end: chartPeriod.value.end }));
const showEvolutionChart = computed(() => monthlySeries.value.length > 1);
const isEvolutionEmpty = computed(() => monthlySeries.value.every((month) => month.orderCount === 0));
const evolutionOptions = computed(() => buildEvolutionOptions(monthlySeries.value, { metric: metric.value, showReturns: monthlySeries.value.some((month) => month.hasReturns), labels: { ordered: t('Ordered'), delivered: t('Delivered'), returned: t('Returns') }, compactAxis: true }));

const heroValue = computed(() => (deliveredActive.value ? totals.value.delivered[metric.value] : totals.value.ordered[metric.value]));
const heroObjective = computed(() => {
	const source = deliveredActive.value ? metricObjectives.value.delivered : metricObjectives.value.ordered;
	return source?.hasObjective ? source.objective : 0;
});
const heroLabel = computed(() => (deliveredActive.value ? t('Delivered') : t('Ordered')));
const heroGauge = computed(() => buildGauge(heroValue.value, heroObjective.value));
const heroFormatted = computed(() => formatMetric(heroValue.value, metric.value, { compact: true }));
const heroParams = computed(() => ({
	value: heroValue.value,
	valueFormatted: heroFormatted.value,
	objective: heroObjective.value,
	objectiveFormatted: '',
	objectiveSuffix: `${formatPercent(heroGauge.value.percentage / 100)} ${t('Obj')}`,
	objectiveSuffixClass: heroGauge.value.classColor,
	barColor: heroGauge.value.color,
}));

const showServiceRate = computed(() => deliveredActive.value);
const serviceRate = computed(() => totals.value.serviceRate[metric.value]);
const orderCountLabel = computed(() => `${showServiceRate.value ? '· ' : ''}${formatNumber(totals.value.orderCount)} ${t('Orders')}`);

const availableMetrics = computed(() => {
	if (!objectives.value.available) return [METRIC.VALUE, METRIC.WEIGHT];
	const list = [];
	if (objectives.value.value.ordered.hasObjective || objectives.value.value.delivered.hasObjective) list.push(METRIC.VALUE);
	if (objectives.value.weight.ordered.hasObjective || objectives.value.weight.delivered.hasObjective) list.push(METRIC.WEIGHT);
	return list.length > 0 ? list : [METRIC.VALUE, METRIC.WEIGHT];
});

const showMetricSwitch = computed(() => availableMetrics.value.length > 1);
const metricItems = computed(() =>
	[
		{ value: METRIC.VALUE, title: t('Turnover') },
		{ value: METRIC.WEIGHT, title: t('Weight') },
	].filter((item) => availableMetrics.value.includes(item.value))
);

const periodLabel = computed(() => {
	const { start, end } = heroPeriod.value;
	return periodPresetLabel(start, end, t) ?? formatPeriodLabel(start, end);
});

async function load() {
	try {
		const [resolvedObjectives, organisations, periodSelection] = await Promise.all([resolveObjectives(), getOrganisations().catch(() => []), loadPeriodSelection()]);
		objectives.value = resolvedObjectives;
		if (!availableMetrics.value.includes(metric.value)) metric.value = availableMetrics.value[0];

		const saved = resolvePeriodSelection(periodSelection, { objectivePeriod: resolvedObjectives.period });
		savedPeriod.value = saved ? { start: saved.startDate, end: saved.endDate } : null;

		const hero = heroPeriod.value;
		const chart = chartPeriod.value;
		const startDate = new Date(Math.min(new Date(hero.start), new Date(chart.start)));
		const endDate = new Date(Math.max(new Date(hero.end), new Date(chart.end)));
		const result = await getDirectOrders({ startDate, endDate });
		if (result.coverage.tooLarge) log('warn', 'OrderTrackingWidget', '⚠️ Period over the network ceiling, showing a partial (truncated) order set');

		const ownerScope = ownerOrganisationScope(organisations, userOrganisationId);
		orders.value = filterOrders(result.orders, { startDate: hero.start, endDate: hero.end, ownerScope });
		chartOrders.value = filterOrders(result.orders, { startDate: chart.start, endDate: chart.end, ownerScope });
	} catch (error) {
		log('warn', 'OrderTrackingWidget', `⚠️ Load failed: ${error.message}`);
		orders.value = [];
		chartOrders.value = [];
	} finally {
		isLoading.value = false;
	}
}

useVisibleRefresh(['main-sync-completed', 'background-sync-completed'], load);

onMounted(load);

// Each metric can carry its own objective period (RG-05), so switching the compact widget's
// Chiffre d'affaires/Poids toggle must re-fetch against the newly active period rather than keep
// showing totals filtered for the previous metric. Skipped while a load is already in flight so the
// initial metric auto-correction in load() (line above) doesn't trigger a redundant second fetch.
watch(metric, () => {
	if (!isLoading.value) load();
});

function toggleFullscreen() {
	isFullscreen.value = !isFullscreen.value;
	if (!isFullscreen.value) load();

	eventBus.emit('widget-fullscreened', {
		widget: 'widget-order-tracking',
		fullScreened: isFullscreen.value,
	});
}

defineExpose({ toggleFullscreen, isFullscreen });
</script>
